import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useActiveGithubOrgForUser } from "./useConnectedGithubOrgs";
import { useApi } from "@/hooks/useApi";
import * as z from "zod";

// Workspace-level types
const SlackWorkspaceStatusSchema = z.object({
  installed: z.boolean(),
  team_id: z.string().nullable().optional(),
  team_name: z.string().nullable().optional()
});
export type SlackWorkspaceStatus = z.infer<typeof SlackWorkspaceStatusSchema>;
const SlackInstallUrlSchema = z.object({
  url: z.string()
});
export const SLACK_INSTALL_SOURCE_CODESMITH_NEW_SESSION_BANNER = "codesmith_new_session_banner" as const;
export const SLACK_INSTALL_SOURCE_CODESMITH_AUTOFIX_INTERSTITIAL = "codesmith_autofix_interstitial" as const;
export const SLACK_INSTALL_SOURCE_CODESMITH_ONBOARDING = "codesmith_onboarding" as const;
export const SLACK_INSTALL_SOURCE_SIDEBAR_FEATURED_BOX = "sidebar_featured_box" as const;
export const SLACK_INSTALL_SOURCE_SLACK_SETTINGS_PAGE = "slack_settings_page" as const;
export const SLACK_INSTALL_SOURCE_SLACK_CHANNEL_PICKER = "slack_channel_picker" as const;
type SlackInstallSource = typeof SLACK_INSTALL_SOURCE_CODESMITH_NEW_SESSION_BANNER | typeof SLACK_INSTALL_SOURCE_CODESMITH_AUTOFIX_INTERSTITIAL | typeof SLACK_INSTALL_SOURCE_CODESMITH_ONBOARDING | typeof SLACK_INSTALL_SOURCE_SIDEBAR_FEATURED_BOX | typeof SLACK_INSTALL_SOURCE_SLACK_SETTINGS_PAGE | typeof SLACK_INSTALL_SOURCE_SLACK_CHANNEL_PICKER;

// User-level types
const SlackLinkStatusSchema = z.object({
  linked: z.boolean(),
  slack_team_id: z.string().nullable(),
  slack_user_id: z.string().nullable(),
  slack_username: z.string().nullable(),
  linked_at: z.string().nullable(),
  link_url: z.string().optional() // Only present when not linked
});
export type SlackLinkStatus = z.infer<typeof SlackLinkStatusSchema>;

// Workspace-level hooks

export function useSlackWorkspaceStatus(orgOverride) {
  const $ = _c(7);
  const api = useApi();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  const org = orgOverride ?? activeGithubOrg;
  let t0;
  if ($[0] !== org) {
    t0 = ["slack-workspace-status", org];
    $[0] = org;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== api || $[3] !== org) {
    t1 = async () => {
      const response = await api.get(`/user/github/orgs/${org}/slack/workspace`);
      return SlackWorkspaceStatusSchema.parse(response.data);
    };
    $[2] = api;
    $[3] = org;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = !!org;
  let t3;
  if ($[5] !== t2) {
    t3 = {
      enabled: t2
    };
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t0, t1, t3);
}
export function useSlackInstallUrl(t0, source, orgOverride, t1) {
  const $ = _c(11);
  const enabled = t0 === undefined ? true : t0;
  const chainUserLink = t1 === undefined ? false : t1;
  const api = useApi();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  const org = orgOverride ?? activeGithubOrg;
  let t2;
  if ($[0] !== chainUserLink || $[1] !== org || $[2] !== source) {
    t2 = ["slack-install-url", org, source, chainUserLink];
    $[0] = chainUserLink;
    $[1] = org;
    $[2] = source;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== api || $[5] !== chainUserLink || $[6] !== org || $[7] !== source) {
    t3 = async () => {
      const response = await api.get(`/user/github/orgs/${org}/slack/install-url`, {
        params: {
          ...(source ? {
            source
          } : {}),
          ...(chainUserLink ? {
            chain_user_link: 1
          } : {})
        }
      });
      const data = SlackInstallUrlSchema.parse(response.data);
      return data.url;
    };
    $[4] = api;
    $[5] = chainUserLink;
    $[6] = org;
    $[7] = source;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = enabled && !!org;
  let t5;
  if ($[9] !== t4) {
    t5 = {
      enabled: t4,
      staleTime: 300000
    };
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return useQuery(t2, t3, t5);
}

/**
 * Records a click on an "Add to Slack" CTA. Best-effort: failures are
 * swallowed so a flaky telemetry endpoint never blocks the OAuth popup.
 */
export function useRecordSlackInstallClick(orgOverride) {
  const $ = _c(3);
  const api = useApi();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  const org = orgOverride ?? activeGithubOrg;
  let t0;
  if ($[0] !== api || $[1] !== org) {
    t0 = async params => {
      if (!org) {
        return;
      }
      try {
        await api.post(`/user/github/orgs/${org}/slack/install-click`, {
          source: params.source
        });
      } catch {}
    };
    $[0] = api;
    $[1] = org;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return useMutation(t0);
}
export function useDisconnectSlackWorkspace() {
  const $ = _c(6);
  const api = useApi();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== activeGithubOrg || $[1] !== api) {
    t0 = async () => {
      await api.delete(`/user/github/orgs/${activeGithubOrg}/slack/workspace`);
    };
    $[0] = activeGithubOrg;
    $[1] = api;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== activeGithubOrg || $[4] !== queryClient) {
    t1 = {
      onSuccess: () => {
        queryClient.invalidateQueries(["slack-workspace-status", activeGithubOrg]);
      }
    };
    $[3] = activeGithubOrg;
    $[4] = queryClient;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return useMutation(t0, t1);
}

// User-level hooks

export function useSlackLinkStatus(orgOverride, options) {
  const $ = _c(7);
  const api = useApi();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  const org = orgOverride ?? activeGithubOrg;
  let t0;
  if ($[0] !== org) {
    t0 = ["slack-link-status", org];
    $[0] = org;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== api || $[3] !== org) {
    t1 = async () => {
      const response = await api.get(`/user/github/orgs/${org}/slack/link`);
      return SlackLinkStatusSchema.parse(response.data);
    };
    $[2] = api;
    $[3] = org;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = (options?.enabled ?? true) && !!org;
  let t3;
  if ($[5] !== t2) {
    t3 = {
      staleTime: 300000,
      enabled: t2
    };
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t0, t1, t3);
}
export function useUnlinkSlackAccount(orgOverride) {
  const $ = _c(6);
  const api = useApi();
  const queryClient = useQueryClient();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  const org = orgOverride ?? activeGithubOrg;
  let t0;
  if ($[0] !== api || $[1] !== org) {
    t0 = async () => {
      await api.delete(`/user/github/orgs/${org}/slack/link`);
    };
    $[0] = api;
    $[1] = org;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== org || $[4] !== queryClient) {
    t1 = {
      onSuccess: () => {
        queryClient.invalidateQueries(["slack-link-status", org]);
      }
    };
    $[3] = org;
    $[4] = queryClient;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return useMutation(t0, t1);
}
export function useSendTestNotification(orgOverride) {
  const $ = _c(3);
  const api = useApi();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  const org = orgOverride ?? activeGithubOrg;
  let t0;
  if ($[0] !== api || $[1] !== org) {
    t0 = async params => {
      await api.post(`/user/github/orgs/${org}/slack/test-notification`, {
        channel_id: params.channelId
      });
    };
    $[0] = api;
    $[1] = org;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return useMutation(t0);
}

// Channel types
export interface SlackChannel {
  id: string;
  name: string;
  is_private: boolean;
}
const SlackChannelsResponseSchema = z.object({
  channels: z.array(z.object({
    id: z.string(),
    name: z.string(),
    is_private: z.boolean()
  })),
  next_cursor: z.string().nullable()
});
export type SlackChannelsResponse = z.infer<typeof SlackChannelsResponseSchema>;

// Cap the wait we honor from a Retry-After response so the loop can't stall
// the page indefinitely if Slack returns a very large value.
export const MAX_RETRY_AFTER_SEC = 60;
export const DEFAULT_RETRY_AFTER_SEC = 30;

// Exported for testing. Returns the number of seconds to wait before retrying
// a 429 response, or null if the error is not a 429 we should retry.
export function parseRetryAfterSeconds(error: unknown): number | null {
  const axiosError = error as {
    response?: {
      status?: number;
      data?: {
        retry_after?: number;
      };
      headers?: Record<string, string>;
    };
  };
  if (axiosError?.response?.status !== 429) return null;
  const body = axiosError.response.data?.retry_after;
  if (typeof body === "number" && body > 0) {
    return Math.min(body, MAX_RETRY_AFTER_SEC);
  }
  const header = axiosError.response.headers?.["retry-after"];
  if (header && !Number.isNaN(Number(header)) && Number(header) > 0) {
    return Math.min(Number(header), MAX_RETRY_AFTER_SEC);
  }
  return DEFAULT_RETRY_AFTER_SEC;
}
export function useSlackChannels(isSlackConnected, orgOverride) {
  const $ = _c(9);
  const api = useApi();
  const queryClient = useQueryClient();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  const org = orgOverride ?? activeGithubOrg;
  let t0;
  if ($[0] !== org) {
    t0 = ["slack-channels", org];
    $[0] = org;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queryKey = t0 as const;
  let t1;
  if ($[2] !== api || $[3] !== org || $[4] !== queryClient || $[5] !== queryKey) {
    t1 = async () => {
      const allChannels = [];
      let cursor = null;
      let retryCount = 0;
      while (true) {
        const params = cursor ? {
          cursor
        } : {};
        let response;
        ;
        try {
          response = await api.get(`/user/github/orgs/${org}/slack/channels`, {
            params
          });
        } catch (t2) {
          const error = t2;
          const retryAfterSec = parseRetryAfterSeconds(error);
          if (retryAfterSec === null) {
            throw error;
          }
          retryCount++;
          if (retryCount > 3) {
            throw error;
          }
          await new Promise(resolve => setTimeout(resolve, retryAfterSec * 1000));
          continue;
        }
        retryCount = 0;
        const page = SlackChannelsResponseSchema.parse(response.data);
        allChannels.push(...page.channels);
        cursor = page.next_cursor;
        queryClient.setQueryData(queryKey, {
          channels: [...allChannels],
          next_cursor: cursor
        });
        if (!cursor) {
          break;
        }
      }
      return {
        channels: allChannels,
        next_cursor: null
      };
    };
    $[2] = api;
    $[3] = org;
    $[4] = queryClient;
    $[5] = queryKey;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const t2 = !!org && isSlackConnected;
  let t3;
  if ($[7] !== t2) {
    t3 = {
      enabled: t2,
      staleTime: 300000,
      retry: false
    };
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(queryKey, t1, t3);
}